import dayjs from 'dayjs';

export type CompetitionType = 'OLYMPIAD' | 'SPORT';
export type CompetitionStatus =
  | 'PLANNED'
  | 'REGISTRATION_OPEN'
  | 'REGISTRATION_CLOSED'
  | 'FINISHED'
  | 'CANCELLED';

export const COMPETITION_STATUS_LABELS: Record<CompetitionStatus, string> = {
  PLANNED: 'Төлөвлөсөн',
  REGISTRATION_OPEN: 'Бүртгэл нээлттэй',
  REGISTRATION_CLOSED: 'Бүртгэл хаагдсан',
  FINISHED: 'Дууссан',
  CANCELLED: 'Цуцлагдсан',
};

export interface ClassYearOption {
  id: number;
  name: string;
  class_number: number;
}

// Score field (CONTEXT.md): one part of the score, e.g. "Бодлого 1", 0–7,
// for the class years that sit it.
export interface ScoreFieldOption {
  id: number;
  name: string;
  sort_order: number | null;
  min_score: number;
  max_score: number;
  class_year_ids: number[];
}

// The Score fields a class year sits.
export const fieldsFor = (fields: ScoreFieldOption[], classYearId: number) =>
  fields.filter((f) => f.class_year_ids.includes(classYearId));

// Award level (CONTEXT.md): Алт, Мөнгө, Хүрэл…, highest first.
export interface AwardLevelOption {
  id: number;
  name: string;
  sort_order: number | null;
}

export interface CompetitionOption {
  id: number;
  name: string;
  type: CompetitionType;
  status: CompetitionStatus | null;
  start_date: string | null;
  end_date: string | null;
  class_years: ClassYearOption[];
  score_fields: ScoreFieldOption[];
  award_levels: AwardLevelOption[];
  // Results (CONTEXT.md): shown on the website while on.
  results_published: boolean;
  // How many of each class year the website lists; null lists everyone.
  results_limit: number | null;
}

export interface RoomOption {
  id: number;
  number: string;
  // Room capacity per class year, set on the room screen.
  capacities: { class_year_id: number; capacity: number }[];
  label: string;
}

// What competition_seats_allocate reports back.
export interface SeatAllocation {
  seated: number;
  unseated: number;
}

export interface CompetitionOptions {
  competitions: CompetitionOption[];
  rooms: RoomOption[];
}

export const COMPETITION_TYPE_LABELS: Record<CompetitionType, string> = {
  OLYMPIAD: 'Олимпиад',
  SPORT: 'Спорт',
};

export interface Participant {
  id: number;
  submission_key: string;
  submitted_at: string | null;
  last_name: string | null;
  first_name: string;
  competition_class_year_id: number;
  class_year_name: string | null;
  class_number: number | null;
  school_name: string | null;
  participant_class_year: string | null;
  mobile_no: string[];
  email_address: string | null;
  participation_no: number | null;
  payment_proof_url: string | null;
  exam_room_id: number | null;
  exam_room_number: string | null;
  exam_number: string | null;
  schedule_start_at: string | null;
  schedule_end_at: string | null;
  schedule_arrival_at: string | null;
}

// A row of the "Оноо" screen (competition_scores): no contact details.
export interface ScoredParticipant {
  id: number;
  last_name: string | null;
  first_name: string;
  school_name: string | null;
  competition_class_year_id: number;
  class_year_name: string | null;
  class_number: number | null;
  exam_number: string | null;
  // Scores by Score field id; a field without a score is missing.
  scores: Record<string, number>;
  // Total score (CONTEXT.md): null until every field of the class year is scored.
  total_score: number | null;
  // Absent (CONTEXT.md): did not sit — no scores, no award.
  is_absent: boolean;
  award_level_id: number | null;
}

// Total score as the server counts it: the sum over the class year's fields,
// null until all of them are scored.
export function totalScore(scores: Record<string, number>, classFields: ScoreFieldOption[]) {
  if (!classFields.length || classFields.some((f) => scores[f.id] === undefined)) return null;
  return Math.round(classFields.reduce((sum, f) => sum + scores[f.id], 0) * 1000) / 1000;
}

// Place within a class year: highest Total score first, equal totals share a
// place and the next total takes the next one (1, 1, 1, 2); unscored get none.
export function placesByTotal(rows: ScoredParticipant[]): Map<number, number> {
  const places = new Map<number, number>();
  const byClass = new Map<number, ScoredParticipant[]>();
  for (const p of rows) {
    if (p.total_score === null) continue;
    byClass.set(p.competition_class_year_id, [
      ...(byClass.get(p.competition_class_year_id) ?? []),
      p,
    ]);
  }
  for (const group of byClass.values()) {
    group.sort((a, b) => (b.total_score ?? 0) - (a.total_score ?? 0));
    let place = 0;
    group.forEach((p, i) => {
      if (i === 0 || group[i - 1].total_score !== p.total_score) place++;
      places.set(p.id, place);
    });
  }
  return places;
}

export type ImportStatus = 'COMPARE' | 'DONE' | 'CANCELLED';
export type ImportActionType = 'CREATE_ONLY' | 'UPDATE_ONLY' | 'CREATE_UPDATE';
export type ImportItemAction = 'CREATE' | 'UPDATE' | 'SKIP' | 'ERROR';

export const IMPORT_STATUS_TAGS: Record<ImportStatus, { label: string; color: string }> = {
  COMPARE: { label: 'Харьцуулах бэлэн', color: 'blue' },
  DONE: { label: 'Дууссан', color: 'green' },
  CANCELLED: { label: 'Цуцлагдсан', color: 'default' },
};

export const IMPORT_ACTION_TYPE_LABELS: Record<ImportActionType, string> = {
  CREATE_UPDATE: 'Нэмэх, шинэчлэх хоёулаа',
  CREATE_ONLY: 'Зөвхөн шинээр нэмэх',
  UPDATE_ONLY: 'Зөвхөн шинэчлэх',
};

export const IMPORT_ITEM_ACTION_TAGS: Record<ImportItemAction, { label: string; color: string }> = {
  CREATE: { label: 'Шинээр нэмэгдэнэ', color: 'green' },
  UPDATE: { label: 'Шинэчлэгдэнэ', color: 'blue' },
  SKIP: { label: 'Алгасагдана', color: 'default' },
  ERROR: { label: 'Алдаатай', color: 'red' },
};

export interface ImportRow {
  id: number;
  created_at: string;
  competition_name: string | null;
  file_name: string;
  action_type: ImportActionType;
  status: ImportStatus;
  total_record: number;
  created_record: number;
  updated_record: number;
  skipped_record: number;
  error_record: number;
  synced_at: string | null;
  created_by_name: string | null;
}

export interface ImportItem {
  id: number;
  row_number: number;
  submission_key: string;
  last_name: string | null;
  first_name: string | null;
  competition_class_year: string | null;
  class_year_name: string | null;
  school_name: string | null;
  mobile_no: string[];
  exam_room: string | null;
  exam_number: string | null;
  payment_proof_url: string | null;
  action: ImportItemAction;
  changes: Record<string, string> | null;
  message: string | null;
}

export interface ImportDetail extends Omit<ImportRow, 'created_at' | 'created_by_name'> {
  competition_id: number;
  columns: string[];
  items: ImportItem[];
}

// BOM so Excel opens Cyrillic as UTF-8.
export function downloadCsv(fileName: string, rows: (string | number | null | undefined)[][]) {
  const quote = (value: string | number | null | undefined) => {
    const text = value == null ? '' : String(value);
    return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };
  const csv = rows.map((row) => row.map(quote).join(',')).join('\n');
  const blob = new Blob([`﻿${csv}\n`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}

export const participantName = (p: { last_name: string | null; first_name: string | null }) =>
  [p.last_name, p.first_name].filter(Boolean).join(' ');

// "Олимпиад 2026 · Олимпиад · 10/10–10/11 · Бүртгэл нээлттэй" — the
// competition dropdown's label.
export function competitionLabel(c: {
  name: string;
  type: CompetitionType;
  status: CompetitionStatus | null;
  start_date: string | null;
  end_date: string | null;
}): string {
  const start = c.start_date ? dayjs(c.start_date).format('MM/DD') : null;
  const end = c.end_date ? dayjs(c.end_date).format('MM/DD') : null;
  const dates = start && end && start !== end ? `${start}–${end}` : (start ?? end);
  return [
    c.name,
    COMPETITION_TYPE_LABELS[c.type],
    dates,
    c.status ? COMPETITION_STATUS_LABELS[c.status] : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

// A Participant's Schedule: "10/11 11:00–13:30 (08:30-д ирэх)".
export function scheduleLabel(p: {
  schedule_start_at: string | null;
  schedule_end_at: string | null;
  schedule_arrival_at: string | null;
}): string {
  if (!p.schedule_start_at) return '';
  const start = dayjs(p.schedule_start_at);
  const end = p.schedule_end_at ? `–${dayjs(p.schedule_end_at).format('HH:mm')}` : '';
  const arrival = p.schedule_arrival_at
    ? ` (${dayjs(p.schedule_arrival_at).format('HH:mm')}-д ирэх)`
    : '';
  return `${start.format('MM/DD HH:mm')}${end}${arrival}`;
}
